import { GearTarget, RARITY_IDS } from "@highmarch/domain";
import type { GearOwner } from "@highmarch/domain";
import { HubScene } from "../../render/hubScene";
import { RARITY_LABEL, gearIconFallback } from "../gearUi";
import { icon } from "../iconsKit";
import { gearIconUrl, portraitUrl, resourceIconUrl } from "../portraits";
import type { ArmouryCtx, GearItem, TileOptions } from "./host";

/** Painted-disc size per tile size (px): the picker's row icon, the "For X" strip, the stash grid. */
const ICON_PX: Record<NonNullable<TileOptions["size"]>, number> = { s: 40, m: 46, l: 50 };
/** Glyph size for the fallback when a painted icon fails to load (a little under the disc). */
const FALLBACK_SCALE = 0.7;

/** Who wears an item, as the art that shows them: the tower's (or the fortress's) portrait at its level's tier. */
export function ownerPortraitUrl(ctx: Pick<ArmouryCtx, "B" | "profile">, owner: GearOwner): string | null {
  const { B, profile } = ctx;
  if (owner.target === GearTarget.Base) return portraitUrl("base", HubScene.tierFor(profile.baseLevel));
  return portraitUrl(B.towers[owner.towerIndex].id, HubScene.tierFor(profile.towerLevels[owner.towerIndex]));
}

/** The owner's display name ("Fortress" for the base). */
export function ownerLabel(ctx: Pick<ArmouryCtx, "B">, owner: GearOwner): string {
  return owner.target === GearTarget.Base ? "Fortress" : ctx.B.towers[owner.towerIndex].name;
}

/**
 * A gold amount with its painted coins (the icon is the label; the accessible name spells it out). With `have`, an
 * amount the player cannot cover is marked `.short` (red) — a cost; with `gain`, it reads "+n" — what salvage pays.
 */
export function resourceHtml(gold: number, opts: { readonly have?: number; readonly gain?: boolean } = {}): string {
  if (gold <= 0) return "";
  const short = opts.have !== undefined && opts.have < gold;
  return `<span class="res au${short ? " short" : ""}" aria-label="${gold} gold"><img decoding="async" src="${resourceIconUrl("gold")}" alt="">${opts.gain ? "+" : ""}${gold}</span>`;
}

/** "+16" / "=" / "−8": a gear-power delta as the tile and row chips print it (a real minus sign, not a hyphen). */
export function deltaText(delta: number): string {
  return delta > 0 ? `+${delta}` : delta < 0 ? `−${-delta}` : "=";
}

/**
 * A square gear tile: the painted icon on its rarity frame (border colour, and a slow sheen for Legendary+), +N, a
 * lock, NEW, the gear-power delta for the context owner (green / grey "=" / red), and the face of the owner wearing
 * it. A `<button>`: the caller wires the tap. Rarity is never colour-only — the accessible name says it in words.
 */
export function gearTile(ctx: Pick<ArmouryCtx, "B" | "profile">, item: GearItem, opts: TileOptions = {}): HTMLElement {
  const B = ctx.B;
  const size = opts.size ?? "m";
  const slot = B.gear.slots[item.slot];
  const el = document.createElement("button");
  el.type = "button";
  el.className = `gtile ${size} r${item.rarity}${opts.isNew ? " isnew" : ""}${item.equippedTo ? " worn" : ""}${opts.delta !== undefined && opts.delta !== null ? " hasdl" : ""}`;
  el.dataset.id = String(item.id);
  const parts: string[] = [`<img class="gi" decoding="async" src="${gearIconUrl(slot.id, RARITY_IDS[item.rarity])}" alt="">`];
  parts.push(`<i class="pl">Lv ${item.level}</i>`);
  if (opts.isNew) parts.push(`<i class="new">NEW</i>`);
  if (item.locked) parts.push(`<i class="lkd">${icon("lock", 11)}</i>`);
  else if (opts.wornChip && item.equippedTo) {
    const face = ownerPortraitUrl(ctx, item.equippedTo);
    if (face) parts.push(`<i class="on"><img decoding="async" src="${face}" alt=""></i>`);
  }
  const d = opts.delta;
  if (d !== undefined && d !== null) parts.push(`<i class="dl ${d > 0 ? "up" : d < 0 ? "dn" : "eq"}">${d > 0 ? icon("bolt", 10) : ""}${deltaText(d)}</i>`);
  el.innerHTML = parts.join("");
  const wornBy = item.equippedTo ? `, on ${ownerLabel(ctx, item.equippedTo)}` : "";
  const gain = d !== undefined && d !== null ? `, ${deltaText(d)} gear` : "";
  el.setAttribute("aria-label", `${RARITY_LABEL[item.rarity]} ${slot.name} Lv ${item.level}${opts.isNew ? ", new" : ""}${wornBy}${gain}`);
  gearIconFallback(el.querySelector<HTMLImageElement>("img.gi"), slot.id, Math.round(ICON_PX[size] * FALLBACK_SCALE));
  return el;
}
